import { Badge, Group, Loader, ScrollArea, SegmentedControl, Stack, Title } from "@mantine/core";
import { useMemo, useState } from "react";
import { ExistingEventCard } from "./ExistingEventCard/ExistingEventCard";
import type { EventVenue, ExistingEventWithStaging } from "./ExistingEventCard/types";
import { EventSort, EventSortOption } from "./ExistingEventsSection/EventSort";

type EventFilter = "upcoming" | "past";

interface ExistingEventsSectionProps {
  events: ExistingEventWithStaging[];
  venues: EventVenue[];
  selectedExistingEvent: ExistingEventWithStaging | null;
  onSelectEvent: (event: ExistingEventWithStaging | null) => void;
  onRemoveStaged: (eventId: number, requestId: number) => void;
  onSave: (eventId: number) => void;
  onSaveVenue: (eventId: number, restaurantId: number | null, afterPartyId: number | null) => void;
  onConfirm: (eventId: number) => void;
  onChangeRequest: (requestId: number, eventId: number) => void;
  onCancel: (eventId: number) => void;
  sortOption: EventSortOption;
  onSortChange: (option: EventSortOption) => void;
  isRefreshing: boolean;
}

export const ExistingEventsSection = ({
  events,
  venues,
  selectedExistingEvent,
  onSelectEvent,
  onRemoveStaged,
  onSave,
  onSaveVenue,
  onConfirm,
  onChangeRequest,
  onCancel,
  sortOption,
  onSortChange,
  isRefreshing,
}: ExistingEventsSectionProps) => {
  const [filter, setFilter] = useState<EventFilter>("upcoming");

  const filteredEvents = useMemo(() => {
    const now = new Date();
    return events.filter((event) => {
      const eventTime = new Date(event.startTime);
      return filter === "upcoming" ? eventTime >= now : eventTime < now;
    });
  }, [events, filter]);

  const handleSelectEvent = (event: ExistingEventWithStaging) => {
    if (selectedExistingEvent?.id === event.id) {
      onSelectEvent(null);
    } else {
      onSelectEvent(event);
    }
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <Title order={2} c="black">
            확정된 이벤트 <Badge size="lg">{filteredEvents.length}</Badge>
          </Title>
          {isRefreshing && <Loader size="xs" />}
        </Group>
        <EventSort value={sortOption} onChange={onSortChange} />
      </Group>
      <SegmentedControl
        value={filter}
        onChange={(value) => setFilter(value as EventFilter)}
        data={[
          { label: "다가오는 오프", value: "upcoming" },
          { label: "지난 오프", value: "past" },
        ]}
        mb="md"
      />
      <ScrollArea style={{ flex: 1 }}>
        <Stack gap="md">
          {filteredEvents.map((event) => (
            <ExistingEventCard
              key={event.id}
              event={event}
              venues={venues}
              isSelected={selectedExistingEvent?.id === event.id}
              onSelect={() => handleSelectEvent(event)}
              onRemoveStaged={(requestId: number) => onRemoveStaged(event.id, requestId)}
              onSave={() => onSave(event.id)}
              onSaveVenue={(restaurantId: number | null, afterPartyId: number | null) =>
                onSaveVenue(event.id, restaurantId, afterPartyId)
              }
              onConfirm={() => onConfirm(event.id)}
              onChangeRequest={(requestId: number) => onChangeRequest(requestId, event.id)}
              onCancel={() => onCancel(event.id)}
            />
          ))}
        </Stack>
      </ScrollArea>
    </div>
  );
};
